import React, { useState, useEffect } from "react";
import { useParams, useLocation, Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import AppLayout from "@/components/layout/app-layout";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAuthenticatedRequest } from "@/hooks/use-authenticated-request";
import { Organization, ProjectWithSummary } from "@/types";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { CalendarIcon, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/use-auth";
import { Alert, AlertDescription } from "@/components/ui/alert";

// Helper function to format date to local YYYY-MM-DD string without timezone issues
const formatDateToLocal = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper function to parse date strings timezone-safely for display
const parseDateSafe = (dateString: string): Date => {
  // Parse YYYY-MM-DD as local date, not UTC
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

// Define a common schema for all project forms - organizations are now optional
const projectSchema = z.object({
  projectName: z.string().min(1, "Project name is required"),
  description: z.string().optional(),
  projectSummary: z.string().optional(),
  location: z.string().optional(),
  status: z.string().optional(),
  ownerOrganizationId: z.string().optional(),
  contractorOrganizationId: z.string().optional(),
  startDate: z.string().nullable().optional(),
  estimatedEndDate: z.string().nullable().optional(),
  contractType: z.string().optional(),
  contractAmount: z.string().optional(),
  contractCurrency: z.string().optional(),
});

export default function ProjectEditPage() {
  const { projectId } = useParams();
  const [_location, navigate] = useLocation();
  const { toast } = useToast();
  const authenticatedRequest = useAuthenticatedRequest();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useAuth();
  
  // Check if this is a new project
  const isNewProject = projectId === "new";
  
  // Check if user can create projects (superadmin needs a tenant context)
  const canCreateProject = !user?.isSuperAdmin || (user?.isSuperAdmin && user?.activeTenantContext && user?.activeTenantContext !== '__NULL__');
  
  // Debug logging
  console.log('[ProjectEditPage] Validation check:', {
    isSuperAdmin: user?.isSuperAdmin,
    activeTenantContext: user?.activeTenantContext,
    canCreateProject
  });

  // Use the same schema for both new projects and editing
  type FormValues = z.infer<typeof projectSchema>;
  
  // Fetch project details for editing
  const { data: projectData, isLoading: isLoadingProject } = useQuery<ProjectWithSummary>({
    queryKey: [`/api/projects/${projectId}`, user?.isSuperAdmin && user?.activeTenantContext],
    queryFn: async () => {
        if (!projectId || isNewProject) return null;
        
        const response = await authenticatedRequest(`/api/projects/${projectId}`);
        if (!response.ok) throw new Error("Failed to fetch project for edit");
        return response.json();
    },
    enabled: !!projectId && !isNewProject,
  });
  
  // Fetch organizations for the dropdown (needed for both new and edit)
  const { data: organizations, isLoading: isLoadingOrgs } = useQuery<Organization[], Error>({
    queryKey: ["/api/organizations", user?.isSuperAdmin ? user.activeTenantContext : user?.tenantId],
    queryFn: async () => {
      const response = await authenticatedRequest("/api/organizations");
      if (!response.ok) {
        throw new Error("Network response was not ok for organizations");
      }
      return response.json();
    },
    enabled: !!user, // Fetch if user is logged in
  });
  
  // Initialize form with appropriate defaults
  const form = useForm<FormValues>({
    resolver: zodResolver(projectSchema),
    defaultValues: isNewProject 
      ? { 
          projectName: ""
        }
      : {
          projectName: "",
          description: "",
          projectSummary: "",
          location: "",
          status: "Planning",
          ownerOrganizationId: "",
          contractorOrganizationId: "",
          startDate: null,
          estimatedEndDate: null,
          contractType: "",
          contractAmount: "",
          contractCurrency: "",
        },
  });
  
  // Update form values when project data is loaded (for editing only)
  useEffect(() => {
    if (projectData && !isNewProject) {
      const project = projectData.project;
      form.reset({
        projectName: project.projectName || "",
        description: project.description || "",
        projectSummary: project.projectSummary || "",
        location: project.location || "",
        status: project.status || "Planning",
        ownerOrganizationId: project.ownerOrganizationId || "",
        contractorOrganizationId: project.contractorOrganizationId || "",
        startDate: project.startDate || null,
        estimatedEndDate: project.estimatedEndDate || null,
        contractType: project.contractType || "",
        contractAmount: project.contractAmount ? project.contractAmount.toString() : "",
        contractCurrency: project.valueCurrency || "",
      });
    }
  }, [projectData, form, isNewProject]);
  
  // Process organizations data
  const ownerOrganizations = organizations?.filter(org => 
    org.organizationType === 'Owner' || org.organizationType === 'Owner\'s Firm'
  ) || [];
  
  const contractorOrganizations = organizations?.filter(org => 
    org.organizationType === 'Contractor'
  ) || [];
  
  // Handle form submission with different behavior for new vs. edit
  const onSubmit = async (data: FormValues) => {
    // Validate tenant context for new projects
    if (isNewProject && !canCreateProject) {
      console.log('[ProjectEditPage] Form submission blocked:', {
        isNewProject,
        canCreateProject,
        isSuperAdmin: user?.isSuperAdmin,
        activeTenantContext: user?.activeTenantContext
      });
      toast({
        title: "Cannot Create Project",
        description: "Please select a specific tenant from the tenant selector to create projects.",
        variant: "destructive",
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      if (isNewProject) {
        // Create project with just the name and let the server assign defaults
        const projectData = {
          projectName: data.projectName
        };
        
        // Create new project with minimal data
        const response = await apiRequest("POST", "/api/projects", projectData);
        
        // Check if response is ok before parsing JSON
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to create project");
        }
        
        const newProject = await response.json();
        
        // Invalidate projects query to refresh the list
        queryClient.invalidateQueries({ queryKey: ["/api/projects", user?.isSuperAdmin ? user.activeTenantContext : user?.tenantId] });
        
        toast({
          title: "Success",
          description: "Project created successfully",
        });
        
        // Navigate to the new project page
        navigate(`/projects/${newProject.projectId}`);
      } else {
        // Process data before submission - handle empty dates and organizations correctly
        const processedProjectData = {
          projectName: data.projectName,
          description: data.description,
          projectSummary: data.projectSummary,
          location: data.location,
          status: data.status,
          // Convert empty strings to null for date fields
          startDate: data.startDate && data.startDate.trim() !== "" ? data.startDate : null,
          estimatedEndDate: data.estimatedEndDate && data.estimatedEndDate.trim() !== "" ? data.estimatedEndDate : null,
          // Convert empty strings to null for organization fields
          ownerOrganizationId: data.ownerOrganizationId && data.ownerOrganizationId.trim() !== "" ? data.ownerOrganizationId : null,
          contractorOrganizationId: data.contractorOrganizationId && data.contractorOrganizationId.trim() !== "" ? data.contractorOrganizationId : null,
        };
        
        // Update existing project with project fields
        const projectResponse = await apiRequest("PUT", `/api/projects/${projectId}`, processedProjectData);
        await projectResponse.json();
        
        // Update contract fields if any are provided
        const contractData: Record<string, unknown> = {};
        if (data.contractType && data.contractType.trim() !== "") {
          contractData.contractType = data.contractType;
        }
        if (data.contractAmount && data.contractAmount.trim() !== "") {
          contractData.contractAmount = parseFloat(data.contractAmount);
        }
        if (data.contractCurrency && data.contractCurrency.trim() !== "") {
          contractData.contractCurrency = data.contractCurrency;
        }
        
        // Only make contract API call if there are contract fields to update
        if (Object.keys(contractData).length > 0) {
          const contractResponse = await apiRequest("PUT", `/api/projects/${projectId}/details`, contractData);
          await contractResponse.json();
        }
        
        // Invalidate queries to refresh data
        queryClient.invalidateQueries({ queryKey: [`/api/projects/${projectId}`] });
        queryClient.invalidateQueries({ queryKey: ["/api/projects", user?.isSuperAdmin ? user.activeTenantContext : user?.tenantId] });
        
        toast({
          title: "Success",
          description: "Project updated successfully",
        });
        
        // Navigate back to project detail page
        navigate(`/projects/${projectId}`);
      }
    } catch (error) {
      // Provide more specific error message for tenant context issues
      const errorMessage = error instanceof Error && error.message.includes("tenant context") 
        ? "Cannot create project without selecting a tenant. Please select a tenant from the top navigation."
        : isNewProject 
          ? "Failed to create project. Please try again." 
          : "Failed to update project. Please try again.";
          
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      console.error("Project submission error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Handle cancel button - always go back to projects list
  const handleCancel = () => {
    navigate("/projects");
  };
  
  // Show different UI for new project vs. editing
  if (isNewProject) {
    // Simple form for creating new project
    return (
      <AppLayout>
        <div className="py-6">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 md:px-8">
            <div className="mb-6">
              <h1 className="text-2xl font-semibold text-neutral-800">Create New Project</h1>
            </div>
            
            {/* Show alert for superadmin when no tenant is selected */}
            {user?.isSuperAdmin && !canCreateProject && (
              <Alert className="mb-6">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  To create projects, please select a specific tenant from the tenant selector in the top navigation.
                </AlertDescription>
              </Alert>
            )}
            
            <Card>
              <CardContent className="pt-6">
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                    <FormField
                      control={form.control}
                      name="projectName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-lg font-semibold">Name</FormLabel>
                          <FormControl>
                            <Input 
                              placeholder="Enter project name" 
                              {...field} 
                              className="text-lg py-6" 
                              autoFocus
                              disabled={!canCreateProject}
                            />
                          </FormControl>
                          <FormDescription className="text-sm mt-2">
                            Details can be added and modified after project creation.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <div className="flex justify-end gap-4 pt-2">
                      <Button
                        variant="outline"
                        type="button"
                        onClick={handleCancel}
                      >
                        Cancel
                      </Button>
                      <Button 
                        type="submit" 
                        disabled={isSubmitting || !canCreateProject} 
                        size="lg"
                        className={!canCreateProject ? "opacity-50 cursor-not-allowed" : ""}
                      >
                        {isSubmitting ? "Creating..." : "Create Project"}
                      </Button>
                    </div>
                  </form>
                </Form>
              </CardContent>
            </Card>
          </div>
        </div>
      </AppLayout>
    );
  }
  
  // Full form for editing existing project
  return (
    <AppLayout>
      <div className="py-6">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 md:px-8">
          <div className="mb-6">
            <h1 className="text-2xl font-semibold text-neutral-800">Edit Project</h1>
          </div>
          
          <Card>
            <CardHeader>
              <CardTitle>Project Details</CardTitle>
            </CardHeader>
            <CardContent>
              {(isLoadingProject || isLoadingOrgs) ? (
                <div className="space-y-4">
                  <div className="bg-muted animate-pulse rounded-md h-8 w-full" />
                  <div className="bg-muted animate-pulse rounded-md h-8 w-full" />
                  <div className="bg-muted animate-pulse rounded-md h-8 w-full" />
                  <div className="bg-muted animate-pulse rounded-md h-20 w-full" />
                </div>
              ) : (
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                    <FormField
                      control={form.control}
                      name="projectName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="font-semibold">Project Name *</FormLabel>
                          <FormControl>
                            <Input placeholder="Enter project name" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <FormField
                        control={form.control}
                        name="status"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Status *</FormLabel>
                            <Select 
                              onValueChange={field.onChange} 
                              defaultValue={field.value}
                              disabled={projectData?.project?.status === 'setup'}
                            >
                              <FormControl>
                                <SelectTrigger className={projectData?.project?.status === 'setup' ? "opacity-50 cursor-not-allowed" : ""}>
                                  <SelectValue placeholder="Select status" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="setup">Setup</SelectItem>
                                <SelectItem value="active">Active</SelectItem>
                                <SelectItem value="complete">Complete</SelectItem>
                              </SelectContent>
                            </Select>
                            {projectData?.project?.status === 'setup' && (
                              <FormDescription className="text-amber-600">
                                Status cannot be changed during project setup
                              </FormDescription>
                            )}
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name="location"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Location</FormLabel>
                            <FormControl>
                              <Input placeholder="Project location" {...field} value={field.value || ""} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <FormField
                        control={form.control}
                        name="startDate"
                        render={({ field }) => (
                          <FormItem className="flex flex-col">
                            <FormLabel>Start Date</FormLabel>
                            <Popover>
                              <PopoverTrigger asChild>
                                <FormControl>
                                  <Button
                                    variant={"outline"}
                                    className={cn(
                                      "w-full pl-3 text-left font-normal",
                                      !field.value && "text-muted-foreground"
                                    )}
                                  >
                                    {field.value ? (
                                      format(parseDateSafe(field.value), "PPP")
                                    ) : (
                                      <span>Pick a date</span>
                                    )}
                                    <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                  </Button>
                                </FormControl>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0" align="start">
                                <Calendar
                                  mode="single"
                                  selected={field.value ? parseDateSafe(field.value) : undefined}
                                  onSelect={(date) => 
                                    field.onChange(date ? formatDateToLocal(date) : null)
                                  }
                                  initialFocus
                                />
                              </PopoverContent>
                            </Popover>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name="estimatedEndDate"
                        render={({ field }) => (
                          <FormItem className="flex flex-col">
                            <FormLabel>Estimated End Date</FormLabel>
                            <Popover>
                              <PopoverTrigger asChild>
                                <FormControl>
                                  <Button
                                    variant={"outline"}
                                    className={cn(
                                      "w-full pl-3 text-left font-normal",
                                      !field.value && "text-muted-foreground"
                                    )}
                                  >
                                    {field.value ? (
                                      format(parseDateSafe(field.value), "PPP")
                                    ) : (
                                      <span>Pick a date</span>
                                    )}
                                    <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                  </Button>
                                </FormControl>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0" align="start">
                                <Calendar
                                  mode="single"
                                  selected={field.value ? parseDateSafe(field.value) : undefined}
                                  onSelect={(date) => 
                                    field.onChange(date ? formatDateToLocal(date) : null)
                                  }
                                  initialFocus
                                />
                              </PopoverContent>
                            </Popover>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <FormField
                        control={form.control}
                        name="ownerOrganizationId"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Owner Organization</FormLabel>
                            <Select 
                              onValueChange={field.onChange} 
                              value={field.value || ""}
                              disabled={ownerOrganizations.length === 0}
                            >
                              <FormControl>
                                <SelectTrigger className={ownerOrganizations.length === 0 ? "opacity-50 cursor-not-allowed" : ""}>
                                  <SelectValue placeholder="Select owner" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {ownerOrganizations.length === 0 ? (
                                  <div className="p-3 text-sm text-muted-foreground flex items-center gap-2">
                                    <AlertCircle className="h-4 w-4 text-amber-500" />
                                    No owner organizations found.{" "}
                                    <Link 
                                      href="/organizations" 
                                      className="text-primary-600 hover:text-primary-800 underline"
                                    >
                                      Create organizations
                                    </Link>
                                  </div>
                                ) : (
                                  ownerOrganizations.map((org) => (
                                    <SelectItem key={org.organizationId} value={org.organizationId}>
                                      {org.name} ({org.organizationType})
                                    </SelectItem>
                                  ))
                                )}
                              </SelectContent>
                            </Select>
                            {ownerOrganizations.length === 0 ? (
                              <FormDescription className="flex items-center gap-2">
                                <AlertCircle className="h-4 w-4 text-amber-500" />
                                No owner organizations found.{" "}
                                <Link 
                                  href="/organizations" 
                                  className="text-primary-600 hover:text-primary-800 underline"
                                >
                                  Create organizations
                                </Link>
                              </FormDescription>
                            ) : (
                              <FormDescription>
                                Select the organization that owns this project.
                              </FormDescription>
                            )}
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name="contractorOrganizationId"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Contractor</FormLabel>
                            <Select 
                              onValueChange={field.onChange} 
                              value={field.value || ""}
                              disabled={contractorOrganizations.length === 0}
                            >
                              <FormControl>
                                <SelectTrigger className={contractorOrganizations.length === 0 ? "opacity-50 cursor-not-allowed" : ""}>
                                  <SelectValue placeholder="Select contractor" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {contractorOrganizations.length === 0 ? (
                                  <div className="p-3 text-sm text-muted-foreground flex items-center gap-2">
                                    <AlertCircle className="h-4 w-4 text-amber-500" />
                                    No contractor organizations found.{" "}
                                    <Link 
                                      href="/organizations" 
                                      className="text-primary-600 hover:text-primary-800 underline"
                                    >
                                      Create organizations
                                    </Link>
                                  </div>
                                ) : (
                                  contractorOrganizations.map((org) => (
                                    <SelectItem key={org.organizationId} value={org.organizationId}>
                                      {org.name} ({org.organizationType})
                                    </SelectItem>
                                  ))
                                )}
                              </SelectContent>
                            </Select>
                            {contractorOrganizations.length === 0 ? (
                              <FormDescription className="flex items-center gap-2">
                                <AlertCircle className="h-4 w-4 text-amber-500" />
                                No contractor organizations found.{" "}
                                <Link 
                                  href="/organizations" 
                                  className="text-primary-600 hover:text-primary-800 underline"
                                >
                                  Create organizations
                                </Link>
                              </FormDescription>
                            ) : (
                              <FormDescription>
                                Select the contracting organization for this project.
                              </FormDescription>
                            )}
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <FormField
                        control={form.control}
                        name="contractType"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Contract Type</FormLabel>
                            <Select 
                              onValueChange={field.onChange} 
                              value={field.value || ""}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select contract type" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="Fixed Price">Fixed Price</SelectItem>
                                <SelectItem value="Time & Materials">Time & Materials</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormDescription>
                              Select the type of contract for this project.
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name="contractAmount"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Contract Amount</FormLabel>
                            <div className="flex gap-2">
                              <FormControl>
                                <Input 
                                  type="number" 
                                  step="0.01"
                                  placeholder="0.00" 
                                  {...field} 
                                  value={field.value || ""}
                                />
                              </FormControl>
                              <FormField
                                control={form.control}
                                name="contractCurrency"
                                render={({ field: currencyField }) => (
                                  <Select 
                                    onValueChange={currencyField.onChange} 
                                    value={currencyField.value || "USD"}
                                  >
                                    <FormControl>
                                      <SelectTrigger className="w-24">
                                        <SelectValue />
                                      </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                      <SelectItem value="USD">USD</SelectItem>
                                      <SelectItem value="EUR">EUR</SelectItem>
                                      <SelectItem value="GBP">GBP</SelectItem>
                                      <SelectItem value="CAD">CAD</SelectItem>
                                      <SelectItem value="AUD">AUD</SelectItem>
                                    </SelectContent>
                                  </Select>
                                )}
                              />
                            </div>
                            <FormDescription>
                              Enter the total contract amount and currency.
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    
                    <FormField
                      control={form.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Description</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="Enter project description"
                              className="min-h-[100px]"
                              {...field}
                              value={field.value || ""}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="projectSummary"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Project Summary</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="Enter project summary"
                              className="min-h-[100px]"
                              {...field}
                              value={field.value || ""}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <div className="flex justify-end gap-4 pt-4">
                      <Button
                        variant="outline"
                        type="button"
                        onClick={handleCancel}
                        className="px-6"
                      >
                        Cancel
                      </Button>
                      <Button type="submit" disabled={isSubmitting} className="px-6">
                        {isSubmitting ? "Saving..." : "Save Changes"}
                      </Button>
                    </div>
                  </form>
                </Form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AppLayout>
  );
}